import { z } from "zod";

/**
 * Schema for Chat.metadata Json field.
 *
 * Stores per-chat context that does not belong in the session metadata or
 * the settings field. Currently used by:
 * - Embed deployments: embeddedMetadata.sessionId identifies the browser session
 * - Search ranking: updatedAt tracks last meaningful activity for recency scoring
 */
export const ChatMetadataSchema = z
  .object({
    /** Direct session ID — set on some older embed chats */
    sessionId: z.string().optional(),
    /** Embed session context — set by the embed SDK on chat creation */
    embeddedMetadata: z
      .object({
        sessionId: z.string().optional()
      })
      .optional(),
    /** ISO date string of last meaningful activity — used by search result ranker */
    updatedAt: z.string().optional(),
    /**
     * Voice call summary — markdown notes extracted by the voice-relay at call end.
     * Surfaced in the inbox sidebar NOTES panel. Mirrors
     * DeploymentSession.callMetadata.sessionNotes which feeds {{sendNotes}}.
     */
    callSummary: z.string().optional()
  })
  // `.passthrough()` keeps arbitrary custom keys on parse. Chat.metadata is an
  // open custom-metadata bag (see the `[key: string]: any` index signature on
  // ChatMetadata): callers attach their own keys via the public metadata CRUD
  // surface. A plain `z.object` strips unknown keys, which would silently drop
  // custom fields wherever metadata is loaded through this schema — e.g. the
  // chat cache reload path (revalidateChatCache → loadChat) and the Public API
  // v1 conversation projection.
  .passthrough();

// Type is defined in shared/domain/chat/chat.types.ts — not duplicated here.
// Use ChatMetadata from @nexus/types for typing; use ChatMetadataSchema for runtime validation.
